import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Box, Checkbox, DropdownFilter, FormGroup, Text, Tooltip } from '@ksyos/design-system';
import { upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import { getSelectedLanguage } from '@frontend/translate';
import { translatePatientTaskType } from '@frontend/translate/custom';
import type { OutpatientClinicTaskListFilter } from '../../types/outpatientClinicTaskListFilter';
import { type GroupedType, useGroupedTypes } from '../../hooks/useGroupedTypes';

export type TypeFilterProps = {
    filter: OutpatientClinicTaskListFilter;
    localType: OutpatientClinicTaskListFilter['type'];
    setFilter: (filter: OutpatientClinicTaskListFilter) => void;
    setLocalType: Dispatch<SetStateAction<OutpatientClinicTaskListFilter['type']>>;
    types: OutpatientClinicTask['type'][];
};

export function TypeFilter({ filter, setFilter, localType, setLocalType, types }: TypeFilterProps) {
    const translate = getSelectedLanguage();
    const [filterCount, setFilterCount] = useState(0);
    const groupedTypes = useGroupedTypes(types);

    const getLocalTypeCount = useCallback(
        (groupTypes: Array<{ ids: string[] }>): string => {
            const selectedCount = groupTypes.filter((groupedType) =>
                groupedType.ids.every((id) => localType?.some((type) => type.id === id))
            ).length;

            return selectedCount > 0 ? ` (${selectedCount})` : '';
        },
        [localType]
    );

    const getFilterTypeCount = useCallback(
        (groupTypes: Array<{ ids: string[] }>): number => {
            return groupTypes.filter((groupedType) =>
                groupedType.ids.every((id) => filter.type?.some((type) => type.id === id))
            ).length;
        },
        [filter.type]
    );

    const handleCheckboxChange = useCallback(
        (ids: string[]) => (event: ChangeEvent<HTMLInputElement>) => {
            setLocalType((prev) =>
                event.target.checked
                    ? [...(prev || []), ...ids.map((id) => ({ id }))]
                    : (prev || []).filter((type) => !ids.includes(type.id))
            );
        },
        [setLocalType]
    );

    const handleReset = useCallback(() => {
        setLocalType(undefined);
        setFilter({ ...filter, type: undefined });
    }, [filter, setFilter, setLocalType]);

    const handleSubmit = useCallback(() => {
        setFilter({
            ...filter,
            type: localType && localType.length > 0 ? localType : undefined,
        });
    }, [filter, localType, setFilter]);

    useEffect(() => {
        const allGroupTypes = Object.values(groupedTypes).flat();
        setFilterCount(getFilterTypeCount(allGroupTypes));
    }, [groupedTypes, getFilterTypeCount]);

    const dropdownOptions = useMemo(() => {
        return Object.entries(groupedTypes)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, groupTypes]) => (
                <DropdownFilter.AccordionItem
                    key={groupName}
                    label={getAccordionLabel(groupName, types) + getLocalTypeCount(groupTypes)}
                    value={groupName}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        {groupTypes
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((groupedType) => (
                                <TypeCheckbox
                                    groupedType={groupedType}
                                    key={groupedType.name}
                                    localType={localType}
                                    onCheckboxChange={handleCheckboxChange}
                                />
                            ))}
                    </Box>
                </DropdownFilter.AccordionItem>
            ));
    }, [groupedTypes, types, localType, handleCheckboxChange, getLocalTypeCount]);

    return (
        <FormGroup label={translate.request.taskType}>
            <DropdownFilter
                count={filterCount}
                label={translate.common.filter}
                noPadding={true}
                onReset={handleReset}
                onSubmit={handleSubmit}
                width="full"
            >
                <DropdownFilter.Accordion type="multiple">{dropdownOptions}</DropdownFilter.Accordion>
            </DropdownFilter>
        </FormGroup>
    );
}

type TypeCheckboxProps = {
    groupedType: GroupedType;
    localType: OutpatientClinicTaskListFilter['type'];
    onCheckboxChange: (ids: string[]) => (event: ChangeEvent<HTMLInputElement>) => void;
};

function TypeCheckbox({ groupedType, localType, onCheckboxChange }: TypeCheckboxProps) {
    const translate = getSelectedLanguage();
    const episodeCategories = Array.from(groupedType.episodeCategories);
    const labels = new Set(
        episodeCategories.map((episodeCategory) => translatePatientTaskType(episodeCategory, groupedType.name))
    );

    const episodeCategoryLabel = getEpisodeCategoryLabel(episodeCategories);

    return (
        <Checkbox
            checked={groupedType.ids.every((id) => localType?.some((type) => type.id === id))}
            key={groupedType.name}
            label={
                <>
                    <Text
                        as="span"
                        size="sm"
                        sx={{ display: 'block' }}
                    >
                        {[...labels].join(' / ')}
                    </Text>
                    {episodeCategories.length === 1 ? (
                        <Text
                            as="span"
                            color="minimal"
                            size="xs"
                            sx={{ display: 'block' }}
                        >
                            {episodeCategoryLabel}
                        </Text>
                    ) : (
                        <Tooltip
                            content={episodeCategoryLabel}
                            placement="right"
                        >
                            <Text
                                as="span"
                                color="minimal"
                                size="xs"
                                sx={{ display: 'block' }}
                            >
                                {episodeCategories.length} {translate.common.carePaths.toLocaleLowerCase()}
                            </Text>
                        </Tooltip>
                    )}
                </>
            }
            onChange={onCheckboxChange(groupedType.ids)}
            value={groupedType.name}
        />
    );
}

function getEpisodeCategoryLabel(episodeCategories: string[]) {
    const translate = getSelectedLanguage();
    const episodeCategoryLabels = episodeCategories.map(
        (episodeCategory) =>
            translate.episodeCategoryEnum[episodeCategory as keyof typeof translate.episodeCategoryEnum]
    );
    const lastEpisodeCategory = [episodeCategoryLabels.pop()];
    const result = [episodeCategoryLabels.join(', '), lastEpisodeCategory]
        .filter(Boolean)
        .join(' ' + translate.common.and + ' ');

    return upperFirst(result.toLocaleLowerCase());
}

function getAccordionLabel(groupName: string, types: OutpatientClinicTask['type'][]) {
    const groupTypes = types.filter((type) => type.group.name === groupName);
    const episodeCategories = groupTypes.map((type) => type.group.episodeCategory.name);
    const labels = new Set(
        episodeCategories.map((episodeCategory) => translatePatientTaskType(episodeCategory, groupName))
    );

    return [...labels].join(' / ');
}
